'use client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Payment, {
  PaymentCallbacks,
  PaymentConfig,
  PaymentStatus,
} from '../../../components/drawer/Payment';
import { usePaymentApi } from '../../../hooks/usePaymentApi';
import { useRplApi } from '../../../hooks/useRplApi';
import { useCourseApi } from '../../../hooks/useCourseApi';
import { usePayFastApi } from '../../../hooks/usePayFastApi';
import { PaymentDTO, RplDTO, CourseDTO } from '../../../services/apiClient';
import { ComponentConstants } from '../../../constants/ComponentConstants';
import { InvoiceStatusConstants } from '../../../constants/InvoiceStatusConstants';

export default function PaymentConfirmation() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payment, setPayment] = useState<PaymentDTO | null>(null);
  const [rpl, setRpl] = useState<RplDTO | null>(null);
  const [course, setCourse] = useState<CourseDTO | null>(null);
  const [payFastFee, setPayFastFee] = useState<number | undefined>();
  const [totalWithPayFastFee, setTotalWithPayFastFee] = useState<number | undefined>();

  const paymentId = searchParams.get('paymentId') || searchParams.get('id');
  const { getById } = usePaymentApi();
  const { getById: getRplById } = useRplApi();
  const { getById: getCourseById } = useCourseApi();
  const { calculateFee } = usePayFastApi();
  const backgroundImage = '/images/NS_Pattern%201_Navy_2025.webp';

  const fetchPayment = async () => {
    if (!paymentId) {
      setError('Payment ID is required');
      setLoading(false);
      return;
    }

    try {
      const response = await getById(paymentId);
      if (response?.data) {
        setPayment(response.data);

        // Fetch related entities based on payment type
        if (response.data.rplId) {
          const rplResponse = await getRplById(response.data.rplId);
          if (rplResponse?.data) {
            setRpl(rplResponse.data);
          }
        }

        if (response.data.courseId) {
          const courseResponse = await getCourseById(response.data.courseId);
          if (courseResponse?.data) {
            setCourse(courseResponse.data);
          }
        }

        // Calculate PayFast fees dynamically
        const amount = response.data.invoice?.totalIncl || 0;
        if (amount > 0) {
          try {
            const feeResponse = await calculateFee(amount);
            if (feeResponse?.data) {
              setPayFastFee(feeResponse.data.payFastFee);
              setTotalWithPayFastFee(feeResponse.data.totalWithFee);
            }
          } catch (feeError) {
            console.error('Error calculating PayFast fees:', feeError);
            // Continue without fees if calculation fails
            setPayFastFee(undefined);
            setTotalWithPayFastFee(undefined);
          }
        }
      } else {
        setError('Payment not found');
      }
    } catch (err: any) {
      console.error('Error fetching payment:', err);
      setError(
        err?.response?.data?.message || 'Failed to load payment details'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayment();
  }, [paymentId]);

  if (loading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-12">
        <div
          className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl text-center z-10">
          <img
            src="/images/logo-linnear-navy.png"
            alt="Natshoot Logo"
            className="mx-auto mb-6 w-56"
          />
          <div className="flex justify-center mb-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
          <p className="text-lg text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-12">
        <div
          className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl text-center z-10">
          <img
            src="/images/logo-linnear-navy.png"
            alt="Natshoot Logo"
            className="mx-auto mb-6 w-56"
          />
          <div className="text-red-600 mb-4">
            <p className="text-xl font-semibold">Payment Not Found</p>
            <p className="text-sm mt-2">{error || 'Unable to load payment details'}</p>
          </div>
          <button
            onClick={() => (window.location.href = '/login')}
            className="mt-4 px-6 py-2 bg-primary text-white rounded hover:bg-primary-dark"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Guard: the invoice behind this link was replaced or cancelled (e.g. a renewal re-priced after
  // an age-group change). Paying it would charge the old amount, so offer no "Pay" button.
  if (payment.isInvoiceDeleted) {
    return (
      <div className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-12">
        <div
          className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 w-full max-w-2xl text-center z-10">
          <img
            src="/images/logo-linnear-navy.png"
            alt="Natshoot Logo"
            className="mx-auto mb-6 w-56"
          />
          <h1 className="text-2xl font-bold mb-2">This Notice Was Replaced</h1>
          <p className="text-gray-600 mb-2">
            Payment notice {payment.invoice?.salesOrderNumber} is no longer valid.
            It was replaced or cancelled.
          </p>
          <p className="text-gray-600 mb-6">
            Please do not pay this notice. If a new notice is due, you will
            receive it by email. For help, please contact{' '}
            <a href="mailto:admin@natshoot.co.za" className="text-primary underline">
              admin@natshoot.co.za
            </a>
            .
          </p>
          <button
            onClick={() => (window.location.href = '/login')}
            className="mt-2 px-6 py-2 bg-primary text-white rounded hover:bg-primary-dark"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Guard: this link stays valid indefinitely (it's what's emailed to the member), so an
  // already-settled invoice must not still offer a "Pay" button - otherwise re-opening an old
  // email, or clicking Pay a second time, re-triggers a real PayFast charge.
  const invoiceAlreadyPaid =
    payment.invoice?.invoiceStatusId?.toLowerCase() ===
      InvoiceStatusConstants.INVOICED || !!payment.invoice?.paidDate;

  if (invoiceAlreadyPaid) {
    return (
      <div className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-12">
        <div
          className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 w-full max-w-2xl text-center z-10">
          <img
            src="/images/logo-linnear-navy.png"
            alt="Natshoot Logo"
            className="mx-auto mb-6 w-56"
          />
          <h1 className="text-2xl font-bold mb-2">Invoice Already Paid</h1>
          <p className="text-gray-600 mb-2">
            Invoice {payment.invoice?.salesOrderNumber} has already been paid
            {payment.invoice?.paidDate
              ? ` on ${new Date(payment.invoice.paidDate).toLocaleDateString()}`
              : ''}
            .
          </p>
          <p className="text-gray-600 mb-6">
            No further payment is required. If you believe this is an error,
            please contact{' '}
            <a href="mailto:admin@natshoot.co.za" className="text-primary underline">
              admin@natshoot.co.za
            </a>
            .
          </p>
          <button
            onClick={() => (window.location.href = '/login')}
            className="mt-2 px-6 py-2 bg-primary text-white rounded hover:bg-primary-dark"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const amount = payment.invoice?.totalIncl || 0;

  const description =
    payment.invoice?.notes || payment.invoice?.invoiceType?.title || '';

  // Determine component type based on payment
  const getComponentId = (): string => {
    if (payment.rplId) return ComponentConstants.RPL;
    if (payment.courseId) return ComponentConstants.DEDICATED_COURSES;
    return ComponentConstants.MEMBERS;
  };

  const getComponentType = (): string => {
    if (payment.rplId) return 'rpl';
    if (payment.courseId) return 'course';
    return 'member';
  };

  const getTitle = (): string => {
    if (payment.rplId) return 'RPL Payment';
    if (payment.courseId) return 'Course Payment';
    return 'Member Payment';
  };

  const paymentConfig: PaymentConfig = {
    amount: amount,
    itemName: getTitle(),
    description: description,
    reference: payment.member?.idPassportNumber || '',
    allowOnlinePayment: true,
    allowEftPayment: true,
    allowDebitOrder: false,
    payFastTransactionFee: payFastFee,
    totalWithPayFastFee: totalWithPayFastFee,
  };

  const paymentStatus: PaymentStatus = {
    isProcessing: false,
    isPending: false,
    isUploaded: false,
    isFailed: false,
    canRetry: true,
  };

  const paymentCallbacks: PaymentCallbacks = {
    onPaymentInitiated: (paymentId) => {
      console.log('Payment initiated:', paymentId);
    },
    onPaymentSuccess: () => {
      console.log('Payment successful');
    },
    onPaymentFailed: () => {
      console.log('Payment failed');
    },
    onProofUploaded: async (fileRecordId) => {
      console.log('Proof uploaded:', fileRecordId);
    },
    onProofPending: async () => {
      console.log('Proof pending');
    },
    onRetryPayment: () => {
      console.log('Retrying payment');
    },
    onCancel: () => {
      window.location.href = '/login';
    },
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-12">
      <div
        className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />

      <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 w-full max-w-2xl z-10">
        <img
          src="/images/logo-linnear-navy.png"
          alt="Natshoot Logo"
          className="mx-auto mb-6 w-56"
        />

        <h1 className="text-2xl font-bold text-center mb-2">
          Complete Your Payment
        </h1>
        <p className="text-center text-gray-600 mb-6">
          Invoice: {payment.invoice?.salesOrderNumber}
        </p>

        <Payment
          member={payment.member!}
          rpl={rpl || undefined}
          course={course || undefined}
          componentId={getComponentId()}
          paymentConfig={paymentConfig}
          paymentStatus={paymentStatus}
          callbacks={paymentCallbacks}
          title={getTitle()}
          showMemberDetails={true}
          type={getComponentType()}
          existingInvoiceId={payment.invoice?.id}
          existingInvoice={payment.invoice!}
        />
      </div>
    </div>
  );
}
